"use client";

import { useEffect, useState } from "react";
import { ButtonLink } from "@/components/ButtonLink";

interface ErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function Error({ error, reset }: ErrorProps) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);

  }, [error]);

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 sm:px-6 lg:px-8 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-gradient-to-br from-[#001640] via-[#0a2554] to-[#143166] opacity-100" />

      {}
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(246, 132, 43, 0.1) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(246, 132, 43, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      {}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {}
        <div
          className={`absolute -top-20 -left-20 w-96 h-96 bg-gradient-to-br from-[#5378BC] to-[#91b2f2] rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob ${
            mounted ? "opacity-30" : "opacity-0"
          } transition-opacity duration-1000`}
        />
        <div
          className={`absolute top-1/4 right-1/4 w-80 h-80 bg-gradient-to-br from-[#f6842b] to-[#f6a362] rounded-full mix-blend-screen filter blur-3xl opacity-25 animate-blob-delayed ${
            mounted ? "opacity-25" : "opacity-0"
          } transition-opacity duration-1000 delay-300`}
        />
        <div
          className={`absolute -bottom-32 left-1/3 w-96 h-96 bg-gradient-to-br from-[#385180] to-[#5378BC] rounded-full mix-blend-screen filter blur-3xl opacity-30 animate-blob-delayed-2 ${
            mounted ? "opacity-30" : "opacity-0"
          } transition-opacity duration-1000 delay-500`}
        />

        {}
        <div
          className={`absolute top-1/2 left-10 w-64 h-64 bg-gradient-to-br from-[#f6842b]/20 to-transparent rounded-full filter blur-2xl animate-blob ${
            mounted ? "opacity-100" : "opacity-0"
          } transition-opacity duration-1000 delay-700`}
        />
        <div
          className={`absolute bottom-20 right-20 w-72 h-72 bg-gradient-to-br from-[#5378BC]/20 to-transparent rounded-full filter blur-2xl animate-blob-delayed ${
            mounted ? "opacity-100" : "opacity-0"
          } transition-opacity duration-1000 delay-900`}
        />
      </div>

      {}
      <div className="absolute top-0 left-0 w-40 h-40 border-t-4 border-l-4 border-[#f6842b]/30 rounded-tl-3xl" />
      <div className="absolute bottom-0 right-0 w-40 h-40 border-b-4 border-r-4 border-[#f6842b]/30 rounded-br-3xl" />

      {}
      <div className="absolute top-0 left-1/4 w-px h-32 bg-gradient-to-b from-[#f6842b]/50 to-transparent animate-pulse" />
      <div
        className="absolute bottom-0 right-1/3 w-px h-32 bg-gradient-to-t from-[#f6842b]/50 to-transparent animate-pulse"
        style={{ animationDelay: "1s" }}
      />

      {}
      <div
        className={`relative z-10 max-w-2xl w-full text-center transform transition-all duration-1000 ${
          mounted ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
        }`}
      >
        {}
        <div className="relative backdrop-blur-xl bg-white/5 rounded-3xl p-8 md:p-12 border border-white/10 shadow-2xl">
          {}
          <div className="absolute inset-0 bg-gradient-to-br from-[#f6842b]/10 via-transparent to-[#5378BC]/10 rounded-3xl" />

          {}
          <div className="mb-8 relative flex justify-center">
            <div
              className={`relative w-24 h-24 md:w-32 md:h-32 transform transition-all duration-1000 delay-200 ${
                mounted ? "scale-100 opacity-100" : "scale-90 opacity-0"
              }`}
            >
              {}
              <svg
                className="w-full h-full"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                style={{
                  filter: "drop-shadow(0 0 30px rgba(246, 132, 43, 0.5))",
                }}
              >
                <defs>
                  <linearGradient
                    id="errorGradient"
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="100%"
                  >
                    <stop offset="0%" stopColor="#f6842b" />
                    <stop offset="50%" stopColor="#5378BC" />
                    <stop offset="100%" stopColor="#f6842b" />
                  </linearGradient>
                </defs>
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  stroke="url(#errorGradient)"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"
                  className="animate-pulse"
                />
              </svg>
              {}
              <div
                className="absolute inset-0 blur-3xl opacity-50 bg-gradient-to-r from-[#f6842b] via-[#5378BC] to-[#f6842b]"
                style={{ animation: "pulse 2s ease-in-out infinite" }}
              />
            </div>
          </div>

          {}
          <div
            className={`mb-8 transform transition-all duration-1000 delay-300 ${
              mounted ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4 drop-shadow-lg">
              Oops! Something Went Wrong
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-transparent via-[#f6842b] to-transparent mx-auto mb-6" />
            <p className="text-base md:text-xl text-white/80 max-w-xl mx-auto leading-relaxed mb-4">
              We encountered an unexpected error. Don&apos;t worry, our team has
              been notified and we&apos;re working on fixing it.
            </p>
          </div>

          {}
          <div
            className={`flex flex-col sm:flex-row gap-4 justify-center items-center relative transform transition-all duration-1000 delay-500 ${
              mounted ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"
            }`}
          >
            <button
              onClick={reset}
              className="group cursor-pointer inline-flex items-center justify-center relative w-full sm:w-auto h-11 bg-gradient-to-r from-[#f6842b] to-[#f6a362] hover:from-[#f6a362] hover:to-[#f6842b] text-white font-bold px-10 py-4 rounded-xl shadow-2xl hover:shadow-[0_0_40px_rgba(246,132,43,0.6)] transform hover:scale-105 transition-all duration-300 overflow-hidden"
            >
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-white/0 via-white/20 to-white/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative flex items-center justify-center">
                <svg
                  className="w-5 h-5 mr-2 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
                Try Again
              </span>
            </button>

            <ButtonLink
              href="/"
              variant="outline"
              size="lg"
              className="group relative w-full sm:w-auto h-11 border-2 border-white/30 hover:border-[#f6842b] text-white hover:text-white font-bold px-10 py-4 rounded-xl shadow-lg hover:shadow-[0_0_30px_rgba(246,132,43,0.4)] backdrop-blur-sm bg-white/5 hover:bg-white/10 transform hover:scale-105 transition-all duration-300 overflow-hidden"
            >
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-[#f6842b]/0 via-[#f6842b]/20 to-[#f6842b]/0 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <span className="relative flex items-center justify-center">
                <svg
                  className="w-5 h-5 mr-2 inline-block"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                Back to Home
              </span>
            </ButtonLink>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#001640] via-[#001640]/50 to-transparent pointer-events-none" />
    </div>
  );
}
